import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import Button from '../../components/Button';
import FileUpload from '../../components/FileUpload';
import FilesList from '../../components/FilesList';
import DisplayDate from '../../components/DisplayDate';
import LoadingSpinner from '../../components/LoadingSpinner';
import FormCheckBox from '../../components/FormCheckBox';

// TYPES
import { RouteParams } from '../../Interfaces';
import { ComplaintAdviceEditCommand } from '../../api/models';

const ContentComplaintsAdviceDetail: React.FC = () => {
	const history = useHistory();

	// Get all Payment Plans from Redux store
	const paymentPlans = useAppSelector((state) => state.lists.paymentPlans);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Get Id from params
	const params = useParams<RouteParams>();

	// Set up data state and initial data
	const initialData: ComplaintAdviceEditCommand = {
		id: undefined,
		title: undefined,
		filename: undefined,
		attachmentUrl: undefined,
		fileDisplayName: undefined,
		order: undefined,
		updated: undefined,
		selectedPlans: []
	};

	const [complaintAdviceDetailData, setComplaintAdviceDetailData] =
		React.useState<ComplaintAdviceEditCommand>(initialData);

	const getContentComplaintAdviceDetailData = async () => {
		if (params.id) {
			setIsLoading(true);

			const response = await apibridge.getComplaintAdviceEdit(params.id);

			response.result = response.result || initialData;

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				setComplaintAdviceDetailData(response.result);
			}
		} else {
			setIsLoading(false);

			setComplaintAdviceDetailData(initialData);
		}
	};

	React.useEffect(() => {
		getContentComplaintAdviceDetailData();
	}, []);

	// On Submit Function
	const onSubmit = async (values: any) => {
		setIsLoading(true);

		const complaintsData: ComplaintAdviceEditCommand = {
			id: values.id,
			title: values.titleInput,
			filename: values.filename,
			order: values.order,
			selectedPlans: values.selectedPlansInput
		};

		const response = await apibridge.postComplaintAdviceEdit(complaintsData);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			history.push('/content-complaints-advice');
		}
	};

	const validationSchema = Yup.object({
		titleInput: Yup.string().required('Required'),
		filename: Yup.string().when('fileDisplayName', { is: undefined, then: Yup.string().required('Required') }),
		selectedPlansInput: Yup.array().min(1, 'Required')
	});

	const initialValues = {
		id: complaintAdviceDetailData.id,
		titleInput: complaintAdviceDetailData.title,
		filename: complaintAdviceDetailData.filename,
		order: complaintAdviceDetailData.order,
		fileDisplayName: complaintAdviceDetailData.fileDisplayName,
		attachmentUrl: complaintAdviceDetailData.attachmentUrl,
		selectedPlansInput: complaintAdviceDetailData.selectedPlans
	};

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form">
						{!!formErrorMessage ? (
							<div className="form-validation-error">{formErrorMessage}</div>
						) : (
							<SwitchTransition mode="out-in">
								<CSSTransition
									key={`key_${isLoading}`}
									timeout={600}
									classNames="standalone-fade"
									mountOnEnter
									unmountOnExit
								>
									{isLoading ? (
										<LoadingSpinner show={isLoading} />
									) : (
										<>
											<div className="general-form-layout-wrapper">
												<div className="form-main">
													<div className="general-divider offset-top-6 mbtom-40"></div>

													<p className="sub-purple-text mbtom-30">
														{complaintAdviceDetailData.updated ? (
															<>
																Last Update <DisplayDate>{complaintAdviceDetailData.updated}</DisplayDate>
															</>
														) : (
															''
														)}
													</p>

													<div className="form-group">
														<label htmlFor="titleInput" className="label">
															Document title
														</label>

														<div className="form-field">
															<Field name="titleInput" id="titleInput" type="text" className="form-input" />

															<ErrorMessage name="titleInput" component="div" className="error-message" />
														</div>
													</div>

													<div className="form-group">
														<label htmlFor="additionalCommentInput" className="label">
															Policy Document
														</label>

														<div className="form-field mbtom-14">
															<FilesList
																onChange={() => {
																	formik.setFieldValue('fileDisplayName', '');
																}}
																multiple={false}
																files={
																	!formik.values.fileDisplayName
																		? []
																		: [
																				{
																					id: formik.values.fileDisplayName,
																					displayFileName: formik.values.fileDisplayName
																				}
																		  ]
																}
															/>

															<FileUpload
																name="filename"
																onChange={(val: string) => {
																	formik.setFieldValue('fileDisplayName', '');
																	formik.setFieldValue('filename', val);
																}}
																multiple={false}
																maxSizeMb={5}
															/>

															<ErrorMessage name="filename" component="div" className="error-message" />
														</div>
													</div>

													<div className="form-group">
														<label htmlFor="selectedPlansInput" className="label mbtom-16 mtop-24">
															Organisations with access
														</label>

														<div className="form-field">
															<FormCheckBox
																values={complaintAdviceDetailData.selectedPlans || []}
																options={paymentPlans}
																onChange={(vals: string[]) => formik.setFieldValue('selectedPlansInput', vals)}
															/>

															<ErrorMessage name="selectedPlansInput" component="div" className="error-message" />
														</div>
													</div>
												</div>
											</div>

											<div className="general-divider"></div>

											<div className="form-button-group">
												<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
													Save
												</Button>
											</div>
										</>
									)}
								</CSSTransition>
							</SwitchTransition>
						)}
					</Form>
				);
			}}
		</Formik>
	);
};

export default ContentComplaintsAdviceDetail;
